/**
 * @fileoverview Generic brand-tinted narrative-insight panel.
 * @description A tinted callout box holding a short list of bulleted (or plain-paragraph)
 * narrative takeaways under a heading — e.g. "Deposits grew 12% YoY, driven mainly by the term
 * book." Ported from the InsightBox Banking design system's own `BusinessSummary`, generalized
 * (icons inlined to match this app's own icon convention instead of `lucide-react`, `title` made a
 * required prop instead of a hardcoded English default) since it's a generic pattern any
 * data-heavy page can use to surface a few plain-language conclusions above the raw numbers.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Rahul Chawda
 */

interface BusinessSummaryPanelProps {
  title: string
  lines: string[]
  variant?: 'icon' | 'plain' | 'bare'
  dense?: boolean
  layout?: 'vertical' | 'horizontal'
}

/**
 * @component
 * @param {BusinessSummaryPanelProps} props - The panel's heading, lines, and layout switches.
 * @param {string} props.title - The panel's heading.
 * @param {string[]} props.lines - The narrative takeaways, one per bullet/paragraph.
 * @param {'icon' | 'plain' | 'bare'} [props.variant] - "icon" (default): circular icon + heading +
 * checkmark bullets, for most detail pages. "plain": uppercase heading, no icon, dot bullets.
 * "bare": mixed-case heading, no icon, no bullets — plain paragraph lines.
 * @param {boolean} [props.dense] - Tighter padding/type scale for pages that must fit one viewport
 * with no scroll. Off by default.
 * @param {'vertical' | 'horizontal'} [props.layout] - "vertical" (default) stacks lines as
 * bullets. "horizontal" lays each line out as its own column (divided by a rule) instead — for a
 * page whose other sections already read left-to-right, so a few short takeaways sit better as
 * parallel columns. Wraps back to stacked columns below the `sm` breakpoint so a longer line still
 * gets room.
 */
function BusinessSummaryPanel({
  title,
  lines,
  variant = 'icon',
  dense = false,
  layout = 'vertical',
}: BusinessSummaryPanelProps) {
  return (
    <div
      className={`rounded-2xl border border-primary-light bg-primary-light/40 ${dense ? 'p-2.5' : 'p-5'}`}
    >
      <div className={`flex items-center gap-2 ${dense ? 'mb-1' : 'mb-3'}`}>
        {variant === 'icon' && (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.8"
            strokeLinecap="round"
            strokeLinejoin="round"
            className="text-primary"
            aria-hidden="true"
          >
            <path d="M7 3.5h7l4 4V19a1.5 1.5 0 0 1-1.5 1.5h-9A1.5 1.5 0 0 1 6 19V5A1.5 1.5 0 0 1 7 3.5Z" />
            <path d="M14 3.5V8h4" />
            <path d="M9 12.5h6M9 16h6" />
          </svg>
        )}
        <h3
          className={`font-bold text-primary ${dense ? 'text-xs' : 'text-sm'} ${variant === 'plain' ? 'tracking-wide uppercase' : ''}`}
        >
          {title}
        </h3>
      </div>
      {variant === 'bare' ? (
        <div className={`flex flex-col ${dense ? 'gap-1' : 'gap-2'}`}>
          {lines.map((line, index) => (
            <p key={index} className={`leading-relaxed text-text ${dense ? 'text-xs' : 'text-sm'}`}>
              {line}
            </p>
          ))}
        </div>
      ) : (
        <ul
          className={`flex flex-col ${dense ? 'gap-1' : 'gap-2'} ${
            layout === 'horizontal'
              ? 'sm:flex-row sm:items-start sm:divide-x sm:divide-primary/20'
              : ''
          }`}
        >
          {lines.map((line, index) => (
            <li
              key={index}
              className={`flex items-start gap-2 leading-relaxed text-text ${dense ? 'text-xs' : 'text-sm'} ${
                layout === 'horizontal'
                  ? `sm:flex-1 first:sm:pl-0 ${dense ? 'sm:px-3' : 'sm:px-5'}`
                  : ''
              }`}
            >
              {variant === 'icon' ? (
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  className="mt-0.5 shrink-0 text-primary"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="9" />
                  <path d="M8.5 12.3l2.4 2.4 4.6-5" />
                </svg>
              ) : (
                <span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
              )}
              <span>{line}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

export default BusinessSummaryPanel
